import { caseName } from '@stash/shared-types';
import type {
  HomeBase,
  StashCard,
  StashCategory,
  Stasher,
  Vendor,
} from '@stash/shared-types';
import type {
  CategoryViewModel,
  NearMeFilter,
  SortKey,
  StashCardPresentation,
  StashCardPeopleScope,
  StashCardViewModel,
} from './types';

// Case names of StashCardActivityType (`{ call: {...} }` → 'call') that count
// as "contacting" the vendor.
const QUALIFYING_ACTIVITY_TYPES = new Set<string>(['call', 'sms', 'email']);

// §3.1 — Scope filtering
export function applyScope(cards: StashCard[], scope: StashCardPeopleScope): StashCard[] {
  if (scope.kind === 'all') return cards;
  if (scope.kind === 'stasher') return cards.filter(c => c.stasherId === scope.stasherId);
  return cards.filter(c => scope.stasherIds.includes(c.stasherId));
}

// §3.5 — Haversine distance in meters
export function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6_371_000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const φ1 = toRad(a.lat);
  const φ2 = toRad(b.lat);
  const Δφ = toRad(b.lat - a.lat);
  const Δλ = toRad(b.lng - a.lng);
  const x =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  return R * c;
}

// §3.2 — HomeBase filtering
export function applyHomeBase(
  cards: StashCard[],
  homeBase: HomeBase,
  scope: StashCardPeopleScope,
  currentUserStasherId: string,
): StashCard[] {
  // Skip filtering when viewing a different stasher's scope
  if (scope.kind === 'stasher' && scope.stasherId !== currentUserStasherId) {
    return cards;
  }

  // geoRadiusRegion is optional on the wire — without one there is no area to
  // filter by, so the HomeBase imposes no restriction.
  if (!homeBase.geoRadiusRegion) return cards;
  const { geoLocation, radiusMeters } = homeBase.geoRadiusRegion;
  const homeCoords = { lat: geoLocation.latitude, lng: geoLocation.longitude };

  return cards.filter(card => {
    const lat = card.vendor.address?.lat;
    const lng = card.vendor.address?.lng;
    if (lat == null || lng == null) return true; // no coordinates → pass through
    return haversineMeters(homeCoords, { lat, lng }) <= radiusMeters;
  });
}

// §3.3 — View model building.
// Phase 5: `vendors` is the streamed canonical vendor list. When resolving the
// effective vendor for a view model, prefer `vendors[card.vendor.recordID]`
// over the embedded `card.vendor` (which may be stale relative to the live
// stream). `vendorOverrides` precedence is unchanged — per-field overrides
// still win over both the streamed vendor and the embedded copy.
export function buildViewModels(
  scopedStashCards: StashCard[],
  stashers: Stasher[],
  vendors: Vendor[],
  sortKey: SortKey,
  currentUserStasherId: string,
  scope: StashCardPeopleScope,
): StashCardViewModel[] {
  const stasherMap = new Map(stashers.map(s => [s.recordID, s]));
  const vendorMap = new Map(vendors.map(v => [v.recordID, v]));

  const resolveVendor = (card: StashCard): Vendor =>
    vendorMap.get(card.vendor.recordID) ?? card.vendor;

  // Group cards by vendor ID
  const groups = new Map<string, StashCard[]>();
  for (const card of scopedStashCards) {
    const vendorId = card.vendor.recordID;
    const group = groups.get(vendorId);
    if (group) {
      group.push(card);
    } else {
      groups.set(vendorId, [card]);
    }
  }

  const viewModels: StashCardViewModel[] = [];

  for (const cards of groups.values()) {
    const firstCard = cards[0]!;

    // §3.3 selection rule
    let selectedStashCard: StashCard;
    if (scope.kind === 'stasher') {
      selectedStashCard =
        cards.find(c => c.stasherId === scope.stasherId) ?? firstCard;
    } else {
      selectedStashCard =
        cards.find(c => c.stasherId === currentUserStasherId) ?? firstCard;
    }

    const relatedStashCards = cards.filter(c => c !== selectedStashCard);

    // Deduplicated stashers for this vendor group
    const seenStasherIds = new Set<string>();
    const stashersUsing: Stasher[] = [];
    for (const card of cards) {
      if (!seenStasherIds.has(card.stasherId)) {
        seenStasherIds.add(card.stasherId);
        const stasher = stasherMap.get(card.stasherId);
        if (stasher) stashersUsing.push(stasher);
      }
    }

    // Streamed vendor preferred; vendorOverrides still take precedence per-field.
    const effectiveVendor = resolveVendor(selectedStashCard);

    const categories =
      selectedStashCard.vendorOverrides?.categories ?? effectiveVendor.categories;

    const displayName =
      selectedStashCard.vendorOverrides?.companyName ??
      effectiveVendor.companyName ??
      effectiveVendor.contactName ??
      '';

    viewModels.push({
      vendor: effectiveVendor,
      // exactOptionalPropertyTypes: omit the key entirely when undefined
      ...(selectedStashCard.vendorOverrides !== undefined
        ? { vendorOverrides: selectedStashCard.vendorOverrides }
        : {}),
      allStashCards: cards,
      selectedStashCard,
      relatedStashCards,
      stashersUsing,
      categories,
      displayName,
    });
  }

  return viewModels.sort((a, b) => sortViewModels(a, b, sortKey));
}

function sortViewModels(a: StashCardViewModel, b: StashCardViewModel, sortKey: SortKey): number {
  if (sortKey === 'displayName') {
    return a.displayName.localeCompare(b.displayName);
  }
  if (sortKey === 'stashCount') {
    return b.allStashCards.length - a.allStashCards.length;
  }
  // recentActivity
  return latestActivityTime(b.allStashCards) - latestActivityTime(a.allStashCards);
}

function latestActivityTime(cards: StashCard[]): number {
  let latest = 0;
  for (const card of cards) {
    for (const activity of card.stashCardActivities ?? []) {
      const t = activity.createdOn.getTime();
      if (t > latest) latest = t;
    }
  }
  return latest;
}

// §3.4 — Near Me and category filtering.
//
// Category filter is descendant-inclusive: tapping any category in the tree
// matches view models whose embedded categories[] contains EITHER the tapped
// category OR any of its descendants. This matches the same set that
// CategoryViewModel.vendorIdsInScope (from buildCategoryTree) aggregates, so
// the count on the category button and the size of the resulting card list
// stay consistent.
//
// `allCategories` is optional for backward compatibility — when omitted, the
// filter falls back to direct-only matching.
export function applyFilters(
  viewModels: StashCardViewModel[],
  nearMeFilter: NearMeFilter,
  scope: StashCardPeopleScope,
  selectedCategoryId: string | null,
  allCategories: StashCategory[] = [],
): StashCardViewModel[] {
  let result = viewModels;

  // Near Me: only when enabled + userLocation + scope === 'all'
  if (nearMeFilter.enabled && nearMeFilter.userLocation !== null && scope.kind === 'all') {
    const userLoc = nearMeFilter.userLocation;
    result = result.filter(vm => {
      const lat = vm.vendorOverrides?.address?.lat ?? vm.vendor.address?.lat;
      const lng = vm.vendorOverrides?.address?.lng ?? vm.vendor.address?.lng;
      if (lat == null || lng == null) return false;
      return haversineMeters(userLoc, { lat, lng }) <= nearMeFilter.radiusMeters;
    });
  }

  // Category filter (descendant-inclusive when allCategories is provided).
  if (selectedCategoryId !== null) {
    const targetSet = collectCategoryAndDescendants(selectedCategoryId, allCategories);
    result = result.filter(vm =>
      vm.categories.some(c => targetSet.has(c.recordID)),
    );
  }

  return result;
}

/**
 * Walk the category tree downward from `rootId`, returning the set of every
 * recordID in that subtree (root included). Cycle-safe — re-entry of a
 * recordID is silently dropped, matching buildCategoryTree's defensive walker.
 */
function collectCategoryAndDescendants(
  rootId: string,
  allCategories: StashCategory[],
): Set<string> {
  const result = new Set<string>([rootId]);
  if (allCategories.length === 0) return result;
  // Pre-index parent → children for O(depth × avg children) BFS instead of a
  // full scan per level.
  const childrenByParent = new Map<string, string[]>();
  for (const c of allCategories) {
    if (typeof c.parentCategoryID !== 'string' || c.parentCategoryID === '') continue;
    let bucket = childrenByParent.get(c.parentCategoryID);
    if (!bucket) {
      bucket = [];
      childrenByParent.set(c.parentCategoryID, bucket);
    }
    bucket.push(c.recordID);
  }
  const queue: string[] = [rootId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    const children = childrenByParent.get(current);
    if (!children) continue;
    for (const child of children) {
      if (result.has(child)) continue; // cycle guard
      result.add(child);
      queue.push(child);
    }
  }
  return result;
}

// §3.7 — Category path string
export function buildCategoryPath(
  category: StashCategory,
  allCategories: StashCategory[],
): string {
  if (!category.parentCategoryID) return category.name;
  const parent = allCategories.find(c => c.recordID === category.parentCategoryID);
  if (!parent) return category.name;
  return `${parent.name} > ${category.name}`;
}

// §3.6 — Category tree building
export function buildCategoryTree(
  viewModels: StashCardViewModel[],
  allCategories: StashCategory[],
  scope: StashCardPeopleScope,
): CategoryViewModel[] {
  // Build categoryId → vendorIds[] map
  const categoryVendorMap = new Map<string, Set<string>>();
  for (const vm of viewModels) {
    for (const cat of vm.categories) {
      const set = categoryVendorMap.get(cat.recordID);
      if (set) {
        set.add(vm.vendor.recordID);
      } else {
        categoryVendorMap.set(cat.recordID, new Set([vm.vendor.recordID]));
      }
    }
  }

  // Filter out self-referencing categories (§3.6 guard — direct A.parent === A)
  const validCategories = allCategories.filter(
    c => c.recordID !== c.parentCategoryID,
  );

  // Indirect-cycle guard. Production data has been observed to carry duplicate
  // recordIDs with conflicting parentCategoryID values, which forms a parent-
  // chain cycle reachable from a top-level root. The recursive walker would
  // otherwise blow the call stack with "Maximum call stack size exceeded".
  // We carry an ancestor-path Set down each branch and short-circuit when a
  // recordID is about to be re-entered. A new Set per recursive call is O(depth)
  // memory, fine for any plausible category-tree depth.
  const buildNode = (cat: StashCategory, ancestors: Set<string>): CategoryViewModel => {
    if (ancestors.has(cat.recordID)) {
      // eslint-disable-next-line no-console
      console.warn(
        `buildCategoryTree: cycle detected at category "${cat.recordID}" — ` +
        `path ${Array.from(ancestors).join(' → ')} → ${cat.recordID}. ` +
        `Truncating subtree.`
      );
      return {
        category: cat,
        subCategories: [],
        vendorIdsInScope: Array.from(categoryVendorMap.get(cat.recordID) ?? new Set<string>()),
        scope,
      };
    }
    const nextAncestors = new Set(ancestors).add(cat.recordID);
    const subCategories = validCategories
      .filter(c => c.parentCategoryID === cat.recordID)
      .map(c => buildNode(c, nextAncestors));

    const vendorIds = new Set<string>(categoryVendorMap.get(cat.recordID));
    for (const sub of subCategories) {
      for (const id of sub.vendorIdsInScope) {
        vendorIds.add(id);
      }
    }

    return {
      category: cat,
      subCategories,
      vendorIdsInScope: Array.from(vendorIds),
      scope,
    };
  };

  // Return only top-level categories (no parentCategoryID or null)
  return validCategories
    .filter(c => !c.parentCategoryID)
    .map(c => buildNode(c, new Set<string>()));
}

// §3.8 — Contacted vendor computation
export function computeMostRecentlyContacted(cards: StashCard[]): string[] {
  type Entry = { lastContactedAt: number; displayName: string };
  const map = new Map<string, Entry>();

  for (const card of cards) {
    for (const activity of card.stashCardActivities ?? []) {
      if (!QUALIFYING_ACTIVITY_TYPES.has(caseName(activity.activityType))) continue;

      const vendorId = card.vendor.recordID;
      const t = activity.createdOn.getTime();
      const displayName =
        card.vendorOverrides?.companyName ??
        card.vendor.companyName ??
        card.vendor.contactName ??
        '';

      const existing = map.get(vendorId);
      if (!existing || t > existing.lastContactedAt) {
        map.set(vendorId, { lastContactedAt: t, displayName });
      }
    }
  }

  return Array.from(map.entries())
    .sort(([, a], [, b]) => {
      if (b.lastContactedAt !== a.lastContactedAt) {
        return b.lastContactedAt - a.lastContactedAt;
      }
      return a.displayName.localeCompare(b.displayName);
    })
    .map(([vendorId]) => vendorId);
}

export function computeMostFrequentlyContacted(cards: StashCard[]): string[] {
  type Entry = { count: number; lastContactedAt: number; displayName: string };
  const map = new Map<string, Entry>();

  for (const card of cards) {
    for (const activity of card.stashCardActivities ?? []) {
      if (!QUALIFYING_ACTIVITY_TYPES.has(caseName(activity.activityType))) continue;

      const vendorId = card.vendor.recordID;
      const t = activity.createdOn.getTime();
      const displayName =
        card.vendorOverrides?.companyName ??
        card.vendor.companyName ??
        card.vendor.contactName ??
        '';

      const existing = map.get(vendorId);
      if (existing) {
        existing.count += 1;
        if (t > existing.lastContactedAt) existing.lastContactedAt = t;
      } else {
        map.set(vendorId, { count: 1, lastContactedAt: t, displayName });
      }
    }
  }

  return Array.from(map.entries())
    .sort(([, a], [, b]) => {
      if (b.count !== a.count) return b.count - a.count;
      if (b.lastContactedAt !== a.lastContactedAt) return b.lastContactedAt - a.lastContactedAt;
      return a.displayName.localeCompare(b.displayName);
    })
    .map(([vendorId]) => vendorId);
}

// §3.9 — Presentation mapping
export function toStashCardPresentation(
  vm: StashCardViewModel,
  allCategories: StashCategory[],
  userLocation?: { lat: number; lng: number },
): StashCardPresentation {
  const firstStasher = vm.stashersUsing[0];
  const firstNote = vm.selectedStashCard?.stashCardNotes?.[0];
  const firstCategory = vm.categories[0];

  const categoryPath = firstCategory
    ? buildCategoryPath(firstCategory, allCategories)
    : '';

  let distanceMeters: number | undefined;
  if (userLocation) {
    const lat = vm.vendorOverrides?.address?.lat ?? vm.vendor.address?.lat;
    const lng = vm.vendorOverrides?.address?.lng ?? vm.vendor.address?.lng;
    if (lat != null && lng != null) {
      distanceMeters = haversineMeters(userLocation, { lat, lng });
    }
  }

  const stashedByName = firstStasher
    ? `${firstStasher.firstName} ${firstStasher.lastName}`
    : '';

  return {
    id: vm.selectedStashCard?.recordID ?? vm.vendor.recordID,
    vendorId: vm.vendor.recordID,
    displayName: vm.displayName,
    categoryPath,
    tags: vm.selectedStashCard?.tags ?? [],
    ...(firstNote !== undefined ? { note: firstNote.detail } : {}),
    stashedBy: {
      name: stashedByName,
      avatarUrl: firstStasher?.profilePhotoIconUrl ?? null,
    },
    stasherCount: vm.stashersUsing.length,
    ...(distanceMeters !== undefined ? { distanceMeters } : {}),
  };
}

// Re-export shared types used by pipeline consumers so they can import from one place
export type { Vendor };
